import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Formik } from 'formik';

import { Form as AntdForm } from 'antd';

import { StyledColumn, StyledContainer, StyledRow } from '../common/LeaseStyles';

import { Divider, TextArea } from '@yuhuinc/ui-library/';

import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import { LeaseCreatorFormItem } from '../common/LeaseCreatorFormItem';
import { FormError } from '../common/FormError';
import { MoneyInput } from '../common/MoneyInput';
import { LeaseRejectedAlert } from '../common/LeaseRejectedAlert';
import RentableItemList from './RentableItemList';
import AdditionalChargeList from './AdditionalChargeList';

import { validateForm } from './LeasePricingFormValidator';
import { getTranslatedString } from 'helpers/i18n';
import { leaseCreatorColors } from 'helpers/ColorHelper';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';
import { media, sizes } from 'helpers/MediaHelper';
import { responsive } from 'helpers/StyleHelper';

import { storeConditionalAddendumsForLease } from '../store/actions';

const FORM_NAME = 'lease_pricing_page';

const StyledFormContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px 16px 0px 16px;
  max-width: 580px;
`;

const StyledContainerOuterForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 0 16px 16px 16px;
  max-width: 580px;
`;

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledPageSubHeader = styled.div`
  font-size: 18px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
  ${media.mobile`
    margin-bottom: 0;
  `};
`;

const StyledHeaderDef = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledDivider = styled(Divider)`
  margin: 29px 0 28px 0;
  ${media.mobile`
    margin: 27px 0;
  `};
`;

const StyledTextArea = styled(TextArea)`
  margin: 0;
`;

const StyledRequiredText = styled.span`
  font-size: ${responsive.text.desktop};
  ${media.mobile`
    font-weight: 400;
    font-size: ${responsive.text.mobile};
  `};
`;

const StyledErrorRow = styled(StyledRow)`
  ${media.mobile`
    margin-bottom: 16px !important;
    `};
`;

const StyledRowBeforeDivider = styled(StyledRow)`
  &,
  #key_deposit_notes {
    margin-bottom: 0;
  }
`;

const LeasePricingPage = ({
  forward,
  back,
  currentPage,
  totalPages,
  leaseFormValues,
  setLeaseFormValues,
  leaseDetails,
  handleCancellation,
  property,
  leaseDraftAdditionalCharges,
  saveDraftLease,
  leaseDraftRentableItems,
  currentRentableItemsForLease,
  currentAdditionalChargesForLease,
  storeConditionalAddendumsForLease,
  conditionalAddendums,
  availableAdditionalCharges,
  latestLeaseReview,
}) => {
  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;
  const labelSize = isMobile ? responsive.text.mobile : responsive.text.desktop;

  const filterForValidAdditionalCharges = (additionalCharges, availableChargesList) => {
    // To dynamically filter out only valid additional charges from the available charges list
    // This is needed in multiple places as filtering for valid additional charges
    // already depends on a list of additional charges and if this additional charges comes from
    // state or redux, it will cause unnecessary re-renders. So rather than storing filtered
    // additional charges, store everything and use this method to filter for valid ones
    // when rendering or saving

    const availableAdditionalChargesId = availableChargesList.map(
      (additionalCharge) => additionalCharge.id
    );

    return additionalCharges.filter((currentAdditionalCharge) =>
      availableAdditionalChargesId.includes(currentAdditionalCharge.id)
    );
  };

  return (
    <StyledContainer>
      <LeaseHeaderProgress lease={leaseDetails} stepsFilled={currentPage} totalSteps={totalPages} />

      <StyledFormContainer>
        <Formik
          initialValues={{
            monthly_rent: (leaseFormValues.monthly_rent ?? leaseDetails.monthly_rent) || 0,
            deposit_amount: (leaseFormValues.deposit_amount ?? leaseDetails.deposit_amount) || 0,
            key_deposit: (leaseFormValues.key_deposit ?? leaseDetails.key_deposit) || 0,
            key_deposit_notes:
              leaseFormValues.key_deposit_notes || leaseDetails.key_deposit_notes || undefined,
            rentable_items: leaseFormValues.rentable_items || [],
            additional_charges: leaseFormValues.additional_charges || [],
          }}
          validate={validateForm}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={(values) => {
            const validAdditionalCharges = filterForValidAdditionalCharges(
              currentAdditionalChargesForLease,
              availableAdditionalCharges
            );

            values.additional_charges = [...validAdditionalCharges];
            values.rentable_items = [...currentRentableItemsForLease];
            if (!isEmpty(conditionalAddendums)) {
              storeConditionalAddendumsForLease(currentRentableItemsForLease, conditionalAddendums);
            }

            setLeaseFormValues((leaseFormValues) => ({
              ...leaseFormValues,
              ...values,
            }));
            saveDraftLease(values);
            forward();
          }}
        >
          {({ handleSubmit, values, touched, errors, setFieldValue, setFieldTouched }) => {
            return (
              <StyledForm id={FORM_NAME} onSubmit={handleSubmit}>
                <AntdForm
                  layout="vertical"
                  initialValues={{
                    monthly_rent: values['monthly_rent'],
                    deposit_amount: values['deposit_amount'],
                    key_deposit: values['key_deposit'],
                    key_deposit_notes: values['key_deposit_notes'],
                    rentable_items: values['rentable_items'],
                    additional_charges: values['additional_charges'],
                  }}
                >
                  <StyledPageHeader>
                    {getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.pricing')}
                  </StyledPageHeader>
                  {latestLeaseReview?.status === 'rejected' && (
                    <StyledRow gutter={16}>
                      <LeaseRejectedAlert latestLeaseReview={latestLeaseReview} />
                    </StyledRow>
                  )}
                  {!isEmpty(errors) && (
                    <StyledErrorRow gutter={16}>
                      <FormError errors={errors} touched={touched} />
                    </StyledErrorRow>
                  )}

                  <StyledPageSubHeader>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePricingPage.leasePricing'
                    )}
                  </StyledPageSubHeader>

                  <StyledRow gutter={16}>
                    <StyledColumn md={8}>
                      <LeaseCreatorFormItem
                        label={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leasePricingPage.monthlyRent'
                        )}
                        labelSize={labelSize}
                        required
                      >
                        <MoneyInput
                          id="monthly_rent"
                          name="monthly_rent"
                          value={values['monthly_rent']}
                          handleChange={(value) => {
                            setFieldTouched('monthly_rent');
                            if (value === '' || value === null) {
                              setFieldValue('monthly_rent', undefined);
                            } else {
                              setFieldValue('monthly_rent', value);
                            }
                          }}
                          isMobile={isMobile}
                          error={touched.monthly_rent && errors.monthly_rent}
                        />
                      </LeaseCreatorFormItem>
                    </StyledColumn>
                    <StyledColumn md={8}>
                      <LeaseCreatorFormItem
                        label={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leasePricingPage.rentalDeposit'
                        )}
                        labelSize={labelSize}
                        required
                      >
                        <MoneyInput
                          id="deposit_amount"
                          name="deposit_amount"
                          value={values['deposit_amount']}
                          handleChange={(value) => {
                            setFieldTouched('deposit_amount');
                            if (value === '' || value === null) {
                              setFieldValue('deposit_amount', undefined);
                            } else {
                              setFieldValue('deposit_amount', value);
                            }
                          }}
                          isMobile={isMobile}
                          error={touched.deposit_amount && errors.deposit_amount}
                        />
                      </LeaseCreatorFormItem>
                    </StyledColumn>
                    <StyledColumn md={8}>
                      <LeaseCreatorFormItem
                        label={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leasePricingPage.keyDeposit'
                        )}
                        labelSize={labelSize}
                        required
                      >
                        <MoneyInput
                          id="key_deposit"
                          name="key_deposit"
                          value={values['key_deposit']}
                          handleChange={(value) => {
                            setFieldTouched('key_deposit');
                            if (value === '' || value === null) {
                              setFieldValue('key_deposit', undefined);
                            } else {
                              setFieldValue('key_deposit', value);
                            }
                          }}
                          isMobile={isMobile}
                          error={touched.key_deposit && errors.key_deposit}
                        />
                      </LeaseCreatorFormItem>
                    </StyledColumn>
                  </StyledRow>

                  <StyledRowBeforeDivider gutter={16}>
                    <StyledColumn md={24}>
                      <LeaseCreatorFormItem label="Key deposit notes" labelSize={labelSize}>
                        <StyledTextArea
                          id="key_deposit_notes"
                          name="key_deposit_notes"
                          value={values['key_deposit_notes']}
                          handleChange={(e) => setFieldValue('key_deposit_notes', e.target.value)}
                          placeholder={getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leasePricingPage.addAKeyDepositNote'
                          )}
                          isMobile={isMobile}
                        />
                      </LeaseCreatorFormItem>
                    </StyledColumn>
                  </StyledRowBeforeDivider>

                  <StyledDivider dashed={false} plain={false} orientation="" type="horizontal" />
                </AntdForm>
              </StyledForm>
            );
          }}
        </Formik>
        <StyledContainerOuterForm>
          {/* Rentable items section */}
          <StyledPageSubHeader>
            {getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.rentableItems')}
          </StyledPageSubHeader>

          <StyledHeaderDef>
            {getTranslatedString(
              'landlordPortal.leaseCreatorV2.leasePricingPage.rentableItemsAssigned'
            )}
          </StyledHeaderDef>

          <StyledRowBeforeDivider gutter={16}>
            <StyledColumn md={24}>
              <RentableItemList
                property={property} // property contains rentable items
                selectedLeaseStartDate={leaseFormValues.lease_start_date} // needed to compare with start date of rentable items
                selectedEarlyMoveInDate={leaseFormValues.early_move_in_date} // needed to compare with start date of rentable items
                selectedLeaseEndDate={leaseFormValues.lease_end_date} // needed when term selected is monthly
                leaseDraftRentableItems={leaseDraftRentableItems}
              />
            </StyledColumn>
          </StyledRowBeforeDivider>

          <StyledDivider dashed={false} plain={false} orientation="" type="horizontal" />

          {/* Additional charges section  */}
          <StyledPageSubHeader>
            {getTranslatedString(
              'landlordPortal.leaseCreatorV2.leasePricingPage.additionalCharges'
            )}
          </StyledPageSubHeader>

          <StyledHeaderDef>
            {getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.extraFeesLabel')}
          </StyledHeaderDef>

          <StyledRow gutter={16}>
            <StyledColumn md={24}>
              <AdditionalChargeList
                leaseDraftAdditionalCharges={leaseDraftAdditionalCharges}
                filterForValidAdditionalCharges={filterForValidAdditionalCharges}
              />
            </StyledColumn>
          </StyledRow>

          <StyledRow mb="0px">
            <StyledColumn md={24}>
              <LeaseCreatorFormItem
                label={<StyledRequiredText>{getTranslatedString('required')}</StyledRequiredText>}
                labelSize={labelSize}
                required
              />
            </StyledColumn>
          </StyledRow>
        </StyledContainerOuterForm>
      </StyledFormContainer>
      <LeaseCreatorPageFooter
        back={back}
        currentPage={currentPage}
        totalPages={totalPages}
        formName={FORM_NAME}
        handleCancellation={handleCancellation}
      />
    </StyledContainer>
  );
};

const mapStoreToProps = (store) => ({
  property: store.landlordProperties.property,
  leaseDraftAdditionalCharges:
    store.landlordApplications.leaseDetails.draft_lease_data.additional_charges,
  leaseDraftRentableItems: store.landlordApplications.leaseDetails.draft_lease_data.rentable_items,
  currentRentableItemsForLease: store.landlord.leaseCreatorV2.currentRentableItemsForLease,
  currentAdditionalChargesForLease: store.landlord.leaseCreatorV2.currentAdditionalChargesForLease,
  conditionalAddendums: store.landlord.leaseCreatorV2.conditionalAddendums,
  availableAdditionalCharges: store.landlord.leaseCreatorV2.availableAdditionalCharges,
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
});

const mapDispatchToProps = { storeConditionalAddendumsForLease };

export default connect(mapStoreToProps, mapDispatchToProps)(LeasePricingPage);
